import ChatbotVoiceSettings, { readConversationSettings, validConversationSettings } from './ChatbotVoiceSettings';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Snackbar,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import chatbotService from '../services/ChatbotService';
import adminAgentService from '../services/AdminAgentService';

const DEFAULT_HEADER_COLOR = '#2563eb';

function normalizeHexColor(value) {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    const withHash = raw.startsWith('#') ? raw : `#${raw}`;
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(withHash) ? withHash.toLowerCase() : raw;
}

function normalizeMultilineList(value) {
    return String(value ?? '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
}

function normalizeLocale(value) {
    const lowered = String(value ?? '').trim().toLowerCase();
    if (lowered === 'ja' || lowered === 'jp' || lowered === 'ja-jp') return 'ja';
    if (lowered === 'en' || lowered === 'en-us') return 'en';
    return 'ja';
}

const SYSTEM_ALLOWED_DOMAIN_HOSTS = new Set([
    'netbot.jp',
    'www.netbot.jp',
    'dashboard.netbot.jp',
]);
function normalizeDomainHost(value) {
    const raw = String(value ?? '').trim().replace(/\/+$/, '');
    if (!raw) return '';
    try {
        const parsed = new URL(raw.includes('://') ? raw : `https://${raw}`);
        return String(parsed.hostname || '').toLowerCase();
    } catch (e) {
        return raw.split('/')[0].split(':')[0].toLowerCase();
    }
}

function filterUserManagedDomains(value) {
    const lines = Array.isArray(value)
        ? value.map((line) => String(line ?? '').trim()).filter(Boolean)
        : normalizeMultilineList(value);
    return lines.filter((line) => !SYSTEM_ALLOWED_DOMAIN_HOSTS.has(normalizeDomainHost(line)));
}

function includesSystemAllowedDomain(value) {
    const lines = Array.isArray(value)
        ? value.map((line) => String(line ?? '').trim()).filter(Boolean)
        : normalizeMultilineList(value);
    return lines.some((line) => SYSTEM_ALLOWED_DOMAIN_HOSTS.has(normalizeDomainHost(line)));
}

function AdminChatbotForm() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { clientId, chatbotId } = useParams();

    const isEdit = Boolean(chatbotId);

    const [vendor, setVendor] = useState('');
    const [projectId, setProjectId] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [agentId, setAgentId] = useState('');
    const [chatbotName, setChatbotName] = useState('');
    const [headerImageUrl, setHeaderImageUrl] = useState('');
    const [avatarImageUrl, setAvatarImageUrl] = useState('');
    const [centerImageUrl, setCenterImageUrl] = useState('');
    const [header, setHeader] = useState('');
    const [headerColor, setHeaderColor] = useState('');
    const [bubbleTextLine1, setBubbleTextLine1] = useState('');
    const [bubbleTextLine2, setBubbleTextLine2] = useState('');
    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [locale, setLocale] = useState('ja');
    const [footerText, setFooterText] = useState('');
    const [footerUrl, setFooterUrl] = useState('');
    const [hidePoweredBy, setHidePoweredBy] = useState(false);
    const [enableLiveAgent, setEnableLiveAgent] = useState(false);
    const [placeholder, setPlaceholder] = useState('');
    const [suggestionsText, setSuggestionsText] = useState('');
    const [domainsText, setDomainsText] = useState('');
    const [launchType, setLaunchType] = useState('Widget');
    const [conversationSettings, setConversationSettings] = useState(() => readConversationSettings());
    const [feedbackType, setFeedbackType] = useState('None');

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [widgetReloadKey, setWidgetReloadKey] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [agents, setAgents] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [agentsError, setAgentsError] = useState('');

    const vendorOptions = useMemo(() => ['VoiceFlow', 'NetBot'], []);

    const normalizedLaunchType = useMemo(() => {
        const raw = String(launchType || '').trim();
        const lowered = raw.toLowerCase();

        if (lowered === 'widget') return 'Widget';
        if (lowered === 'popover') return 'Popover';
        if (lowered === 'embed' || lowered === 'ebed') return 'Embed';

        return 'Widget';
    }, [launchType]);

    const normalizedFeedbackType = useMemo(() => {
        const raw = String(feedbackType || '').trim();
        const lowered = raw.toLowerCase();

        if (!lowered || lowered === 'none') return 'None';
        if (lowered === 'thumbs' || lowered === 'thumb') return 'Thumbs';
        if (lowered === 'star' || lowered === 'stars') return 'Stars';

        return 'None';
    }, [feedbackType]);

    const normalizedLocale = useMemo(() => normalizeLocale(locale), [locale]);

    const normalizedVendor = useMemo(() => String(vendor || '').trim(), [vendor]);
    const isNetBotVendor = normalizedVendor === 'NetBot';

    useEffect(() => {
        if (!clientId || !isNetBotVendor) return;

        let cancelled = false;
        const loadAgents = async () => {
            try {
                setAgentsError('');
                setAgentsLoading(true);
                const list = await adminAgentService.getAgents(clientId);
                if (cancelled) return;
                setAgents(Array.isArray(list) ? list : []);
            } catch (e) {
                console.error('Failed to load agents for chatbot', e);
                if (!cancelled) {
                    setAgents([]);
                    setAgentsError(t('adminChatbotForm.failedToLoadAgents', 'Failed to load agents'));
                }
            } finally {
                if (!cancelled) setAgentsLoading(false);
            }
        };

        loadAgents();
        return () => {
            cancelled = true;
        };
    }, [clientId, isNetBotVendor, t]);

    const agentIdExistsInList = useMemo(() => {
        const current = String(agentId || '').trim();
        if (!current) return true;
        return (Array.isArray(agents) ? agents : []).some((a) => String(a?.agentId || '').trim() === current);
    }, [agents, agentId]);

    const initialFromState = useMemo(() => {
        const state = location.state;
        if (state && state.chatbot && typeof state.chatbot === 'object') return state.chatbot;
        return null;
    }, [location.state]);

    const embedCode = useMemo(() => {
        if (!chatbotId) return '';
        return `<script src="https://widget.chatbot.netbot.jp/bundle.iife.js?v=1" data-chatbot-id="${chatbotId}" async>
</script>`;
    }, [chatbotId]);

    const handleCopyEmbedCode = async () => {
        try {
            await navigator.clipboard.writeText(embedCode);
            setSnackbar({ open: true, message: t('adminChatbotForm.embedCodeCopied', 'Embed code copied'), severity: 'success' });
        } catch (e) {
            console.error('Failed to copy embed code', e);
            setSnackbar({ open: true, message: t('adminChatbotForm.failedToCopyEmbedCode', 'Failed to copy embed code'), severity: 'error' });
        }
    };

    useEffect(() => {
        const hydrate = (bot) => {
            setVendor(bot?.vendor || '');
            setProjectId(bot?.projectId || '');
            setApiKey(bot?.apiKey || '');
            setAgentId(bot?.agentId || '');
            setChatbotName(bot?.chatbotName || bot?.name || '');
            setHeaderImageUrl(bot?.headerImageUrl || bot?.imageUrl || '');
            setAvatarImageUrl(bot?.avatarImageUrl || bot?.imageUrl || '');
            setCenterImageUrl(bot?.centerImageUrl || bot?.imageUrl || '');
            setHeader(bot?.header || '');
            setHeaderColor(bot?.headerColor || '');
            setBubbleTextLine1(bot?.bubbleTextLine1 || '');
            setBubbleTextLine2(bot?.bubbleTextLine2 || '');
            setWelcomeMessage(bot?.welcomeMessage || '');
            setLocale(normalizeLocale(bot?.locale));
            setFooterText(bot?.footerText || '');
            setFooterUrl(bot?.footerUrl || '');
            setHidePoweredBy(Boolean(bot?.hidePoweredBy));
            setEnableLiveAgent(Boolean(bot?.enableLiveAgent));
            setPlaceholder(bot?.placeholder || '');
            setLaunchType(bot?.launchType || 'Widget');
            setConversationSettings(readConversationSettings(bot || {}));
            setFeedbackType(bot?.feedbackType || 'None');

            if (Array.isArray(bot?.initSuggestions)) {
                setSuggestionsText(bot.initSuggestions.filter(Boolean).join('\n'));
            } else {
                setSuggestionsText('');
            }
            if (Array.isArray(bot?.domains)) {
                setDomainsText(filterUserManagedDomains(bot.domains).join('\n'));
            } else {
                setDomainsText('');
            }
        };

        const load = async () => {
            if (!clientId || !chatbotId) return;
            try {
                setLoading(true);
                const found = await chatbotService.getAdminChatbot(clientId, chatbotId);
                if (found) {
                    hydrate(found);
                } else {
                    setSnackbar({ open: true, message: t('adminChatbotForm.chatbotNotFound', 'Chatbot not found'), severity: 'error' });
                }
            } catch (e) {
                console.error('Failed to load admin chatbot', e);
                setSnackbar({ open: true, message: t('adminChatbotForm.failedToLoadChatbot', 'Failed to load chatbot'), severity: 'error' });
            } finally {
                setLoading(false);
            }
        };
        if (isEdit) {
            if (initialFromState) {
                hydrate(initialFromState);
            } else {
                load();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, chatbotId, isEdit]);

    const buildPayload = useMemo(() => {
        const lines = normalizeMultilineList(suggestionsText);
        const domains = filterUserManagedDomains(domainsText);

        const vendorValue = String(vendor || '').trim();
        const isNetBot = vendorValue === 'NetBot';

        return {
            payload: {
                vendor: vendorValue || undefined,
                projectId: !isNetBot ? (projectId || undefined) : undefined,
                apiKey: !isNetBot ? (apiKey || undefined) : undefined,
                agentId: isNetBot ? (agentId || undefined) : undefined,
                chatbotName: chatbotName || undefined,
                headerImageUrl: headerImageUrl || undefined,
                avatarImageUrl: avatarImageUrl || undefined,
                centerImageUrl: centerImageUrl || undefined,
                header: header || undefined,
                headerColor: normalizeHexColor(headerColor) || undefined,
                bubbleTextLine1: bubbleTextLine1 || undefined,
                bubbleTextLine2: bubbleTextLine2 || undefined,
                welcomeMessage: welcomeMessage || undefined,
                locale: normalizedLocale,
                footerText: footerText || undefined,
                footerUrl: footerUrl || undefined,
                hidePoweredBy: Boolean(hidePoweredBy),
                enableLiveAgent: Boolean(enableLiveAgent),
                placeholder: placeholder || undefined,
                launchType: normalizedLaunchType || undefined,
                ...conversationSettings,
                feedbackType: normalizedFeedbackType || undefined,
                initSuggestions: lines,
                domains,
            },
            lines,
            domains,
        };
    }, [
        suggestionsText,
        vendor,
        projectId,
        apiKey,
        agentId,
        chatbotName,
        headerImageUrl,
        avatarImageUrl,
        centerImageUrl,
        header,
        headerColor,
        bubbleTextLine1,
        bubbleTextLine2,
        welcomeMessage,
        normalizedLocale,
        footerText,
        footerUrl,
        hidePoweredBy,
        enableLiveAgent,
        placeholder,
        normalizedLaunchType,
        conversationSettings,
        normalizedFeedbackType,
        domainsText,
    ]);

    const handleCancel = () => {
        if (clientId) {
            navigate(`/admin/clients/${encodeURIComponent(clientId)}/chatbots`);
        } else {
            navigate('/customer-list');
        }
    };

    const handleDomainsTextChange = (value) => {
        setDomainsText(value);
        if (includesSystemAllowedDomain(value)) {
            setSnackbar({ open: true, message: t('adminChatbotForm.systemAllowedDomainMessage', 'That domain is already in the system default CORS list.'), severity: 'info' });
        }
    };

    const handleSave = async () => {
        if (!clientId) return;

        const { payload, lines, domains } = buildPayload;

        // For "new", don't create an empty chatbot.
        const isNetBot = String(payload.vendor || '').trim() === 'NetBot';

        const hasAnyValue = Boolean(
            (payload.vendor && String(payload.vendor).trim()) ||
                (!isNetBot && payload.projectId && String(payload.projectId).trim()) ||
                (!isNetBot && payload.apiKey && String(payload.apiKey).trim()) ||
                (isNetBot && payload.agentId && String(payload.agentId).trim()) ||
                (payload.chatbotName && String(payload.chatbotName).trim()) ||
                (payload.header && String(payload.header).trim()) ||
                (payload.headerColor && String(payload.headerColor).trim()) ||
                (payload.bubbleTextLine1 && String(payload.bubbleTextLine1).trim()) ||
                (payload.bubbleTextLine2 && String(payload.bubbleTextLine2).trim()) ||
                (payload.welcomeMessage && String(payload.welcomeMessage).trim()) ||
                payload.locale !== 'ja' ||
                (payload.footerText && String(payload.footerText).trim()) ||
                (payload.footerUrl && String(payload.footerUrl).trim()) ||
                payload.hidePoweredBy === true ||
                payload.enableLiveAgent === true ||
                (payload.placeholder && String(payload.placeholder).trim()) ||
                (domains && domains.length > 0) ||
                (lines && lines.length > 0) ||
                normalizedLaunchType !== 'Widget' ||
                normalizedFeedbackType !== 'None'
        );

        if (!isEdit && !hasAnyValue) {
            setSnackbar({ open: true, message: t('adminChatbotForm.fillAtLeastOneField', 'Fill at least one field before creating.'), severity: 'error' });
            return;
        }

        try {
            setSaving(true);
            if (isEdit) {
                await chatbotService.updateAdminChatbot(clientId, chatbotId, payload);
                setSuggestionsText(lines.join('\n'));
                setDomainsText(domains.join('\n'));
                if (normalizedLaunchType !== 'Embed') {
                    setWidgetReloadKey((v) => v + 1);
                }
                setSnackbar({ open: true, message: t('adminChatbotForm.chatbotUpdated', 'Chatbot updated'), severity: 'success' });
            } else {
                const created = await chatbotService.createAdminChatbot(clientId, payload);
                const createdId = created?.chatbotId;
                setSnackbar({ open: true, message: t('adminChatbotForm.chatbotCreated', 'Chatbot created'), severity: 'success' });
                if (createdId) {
                    navigate(
                        `/admin/clients/${encodeURIComponent(clientId)}/chatbots/${encodeURIComponent(createdId)}`,
                        { state: { chatbot: created } }
                    );
                }
            }
        } catch (e) {
            console.error('Failed to save admin chatbot', e);
            setSnackbar({ open: true, message: t('adminChatbotForm.failedToSaveChatbot', 'Failed to save chatbot'), severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!isEdit || !chatbotId) return;
        if (normalizedLaunchType === 'Embed') return;

        let script = document.querySelector('script[data-netbot-widget="true"]');
        let listenerAdded = false;

        const loadWidget = () => {
            if (window.netbot && window.netbot.chat && typeof window.netbot.chat.load === 'function') {
                window.netbot.chat.load({ chatbotId });
            }
        };

        if (!script) {
            script = document.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.setAttribute('data-netbot-widget', 'true');
            script.setAttribute('data-chatbot-id', chatbotId);
            script.src = 'https://widget.chatbot.netbot.jp/bundle.iife.js?v=1';
            script.onload = loadWidget;
            document.body.appendChild(script);
        } else {
            script.setAttribute('data-chatbot-id', chatbotId);

            if (script.getAttribute('data-loaded') === 'true') {
                loadWidget();
            } else {
                script.addEventListener('load', loadWidget);
                listenerAdded = true;
            }
        }

        return () => {
            try {
                if (window.netbot && window.netbot.chat) {
                    if (typeof window.netbot.chat.unload === 'function') {
                        window.netbot.chat.unload();
                    } else if (typeof window.netbot.chat.hide === 'function') {
                        window.netbot.chat.hide();
                    } else if (typeof window.netbot.chat.destroy === 'function') {
                        window.netbot.chat.destroy();
                    }
                }
            } catch (e) {}

            if (listenerAdded && script) {
                script.removeEventListener('load', loadWidget);
            }

            const existingScript = document.querySelector('script[data-netbot-widget="true"]');
            if (existingScript && existingScript.parentNode) {
                existingScript.parentNode.removeChild(existingScript);
            }

            const iframes = document.querySelectorAll('iframe');
            iframes.forEach((iframe) => {
                const src = iframe.getAttribute('src') || '';
                if ((src.includes('widget.chatbot.netbot.jp') || src.includes('chatbot.netbot.jp')) && iframe.parentNode) {
                    iframe.parentNode.removeChild(iframe);
                }
            });

            const widgetButtonSelectors = [
                '[class*="netbot"]',
                '[class*="chatbot"]',
                '[id*="netbot"]',
                '[id*="chatbot"]',
                '[data-chatbot-id]',
                '[data-netbot-widget-root]',
                'div[style*="z-index"]',
                'button[style*="z-index"]',
                'button[aria-label*="NetBot"]',
                'button[aria-label*="chatbot"]',
                'button[aria-label*="netbot"]',
            ];

            document.querySelectorAll(widgetButtonSelectors.join(',')).forEach((node) => {
                if (node && node.parentNode && node.id !== 'root') {
                    try {
                        node.parentNode.removeChild(node);
                    } catch (e) {}
                }
            });
        };
    }, [isEdit, chatbotId, normalizedLaunchType, widgetReloadKey]);

    const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    return (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                }}
            >
                <Box>
                    <Typography variant="h5" fontWeight="bold">
                        {isEdit ? t('adminChatbotForm.editChatbot', 'Edit Chatbot') : t('adminChatbotForm.newChatbot', 'New Chatbot')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('adminChatbotForm.clientLabel', 'Client: {{client}}', { client: clientId || '-' })}
                        {'  '}•{'  '}
                        {t('adminChatbotForm.chatbotLabel', 'Chatbot: {{chatbot}}', { chatbot: isEdit ? (chatbotId || '-') : t('adminChatbotForm.newParenthetical', '(new)') })}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button variant="outlined" onClick={handleCancel} disabled={loading || saving}>
                        {t('adminChatbotForm.cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading || saving || !clientId || !validConversationSettings(conversationSettings)}
                    >
                        {isEdit ? t('adminChatbotForm.save', 'Save') : t('adminChatbotForm.create', 'Create')}
                    </Button>

                    <Button variant="outlined" onClick={() => navigate('/customer-list')} disabled={loading || saving}>
                        {t('adminChatbotForm.backToClients', 'Back to Clients')}
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Paper
                    sx={{
                        flex: isEdit ? '0 0 50%' : '1 1 100%',
                        minWidth: 320,
                        p: 2,
                    }}
                    elevation={1}
                >
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                        {t('adminChatbotForm.chatbotSettings', 'Chatbot Settings')}
                    </Typography>

                    <TextField
                        label={t('adminChatbotForm.name', 'Name')}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        value={chatbotName}
                        onChange={(e) => setChatbotName(e.target.value)}
                        disabled={loading || saving}
                    />

                    <TextField
                        label={t('adminChatbotForm.vendor', 'Vendor')}
                        select
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        value={vendor}
                        onChange={(e) => setVendor(e.target.value)}
                        disabled={loading || saving}
                    >
                        {(() => {
                            const current = String(vendor || '').trim();
                            const exists = current && vendorOptions.includes(current);
                            if (current && !exists) {
                                return <MenuItem value={current}>{t('adminChatbotForm.current', 'Current: {{value}}', { value: current })}</MenuItem>;
                            }
                            return null;
                        })()}
                        {vendorOptions.map((v) => (
                            <MenuItem key={v} value={v}>{v}</MenuItem>
                        ))}
                    </TextField>

                    {isNetBotVendor ? (
                        <TextField
                            label={t('adminChatbotForm.agent', 'Agent')}
                            select
                            fullWidth
                            size="small"
                            sx={{ mb: 2 }}
                            value={agentId}
                            onChange={(e) => setAgentId(e.target.value)}
                            disabled={loading || saving}
                            helperText={agentsLoading ? t('adminChatbotForm.loadingAgents', 'Loading agents…') : (agentsError || (agents?.length ? '' : t('adminChatbotForm.noAgentsFound', 'No agents found')))}
                        >
                            <MenuItem value="">{t('adminChatbotForm.selectAgent', 'Select agent')}</MenuItem>
                            {String(agentId || '').trim() && !agentIdExistsInList ? (
                                <MenuItem value={String(agentId || '').trim()}>{t('adminChatbotForm.current', 'Current: {{value}}', { value: String(agentId || '').trim() })}</MenuItem>
                            ) : null}
                            {(Array.isArray(agents) ? agents : []).map((a) => {
                                const id = String(a?.agentId || '').trim();
                                if (!id) return null;
                                const label = String(a?.agentName || '').trim() || id;
                                return (
                                    <MenuItem key={id} value={id}>
                                        {label}
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                    ) : (
                        <>
                            <TextField
                                label={t('adminChatbotForm.projectId', 'Project ID')}
                                fullWidth
                                size="small"
                                sx={{ mb: 2 }}
                                value={projectId}
                                onChange={(e) => setProjectId(e.target.value)}
                                disabled={loading || saving}
                            />

                            <TextField
                                label={t('adminChatbotForm.apiKey', 'API Key')}
                                fullWidth
                                size="small"
                                sx={{ mb: 2 }}
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                disabled={loading || saving}
                            />
                        </>
                    )}

                    <ChatbotVoiceSettings value={conversationSettings} onChange={setConversationSettings}
                        disabled={loading || saving} voiceAllowed={vendor === 'NetBot'} />
                    <TextField
                        label={t('adminChatbotForm.launchType', 'Launch Type')}
                        select
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        value={normalizedLaunchType}
                        onChange={(e) => setLaunchType(e.target.value)}
                        disabled={loading || saving}
                    >
                        <MenuItem value="Widget">{t('adminChatbotForm.launchTypeWidget', 'Widget')}</MenuItem>
                        <MenuItem value="Popover">{t('adminChatbotForm.launchTypePopover', 'Popover')}</MenuItem>
                        <MenuItem value="Embed">{t('adminChatbotForm.launchTypeEmbed', 'Embed')}</MenuItem>
                    </TextField>

                    <TextField
                        label={t('adminChatbotForm.feedbackType', 'Feedback Type')}
                        select
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        value={normalizedFeedbackType}
                        onChange={(e) => setFeedbackType(e.target.value)}
                        disabled={loading || saving}
                    >
                        <MenuItem value="None">{t('adminChatbotForm.feedbackTypeNone', 'None')}</MenuItem>
                        <MenuItem value="Thumbs">{t('adminChatbotForm.feedbackTypeThumbs', 'Thumbs')}</MenuItem>
                        <MenuItem value="Stars">{t('adminChatbotForm.feedbackTypeStars', 'Stars')}</MenuItem>
                    </TextField>

                    <TextField
                        label={t('adminChatbotForm.header', 'Header')}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        value={header}
                        onChange={(e) => setHeader(e.target.value)}
                        disabled={loading || saving}
                    />

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                            {t('adminChatbotForm.headerColor', 'Header Bar Color')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                component="input"
                                type="color"
                                value={normalizeHexColor(headerColor) || DEFAULT_HEADER_COLOR}
                                onChange={(e) => setHeaderColor(e.target.value)}
                                disabled={loading || saving}
                                aria-label={t('adminChatbotForm.headerColor', 'Header Bar Color')}
                                sx={{
                                    width: 48,
                                    height: 40,
                                    p: 0,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    background: 'none',
                                    cursor: 'pointer',
                                    flex: '0 0 auto',
                                }}
                            />
                            <TextField
                                size="small"
                                placeholder={DEFAULT_HEADER_COLOR}
                                value={headerColor}
                                onChange={(e) => setHeaderColor(e.target.value)}
                                disabled={loading || saving}
                                sx={{ maxWidth: 160 }}
                            />
                            {headerColor ? (
                                <Button
                                    size="small"
                                    onClick={() => setHeaderColor('')}
                                    disabled={loading || saving}
                                >
                                    {t('adminChatbotForm.resetColor', 'Reset')}
                                </Button>
                            ) : null}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {t('adminChatbotForm.headerColorHelper', 'Color of the chat header bar. Suggestion buttons use a matching lighter shade. Leave blank to use the default.')}
                        </Typography>
                    </Box>

                    <TextField
                        label={t('adminChatbotForm.welcomeMessage', 'Welcome Message')}
                        multiline
                        fullWidth
                        minRows={3}
                        size="small"
                        sx={{ mb: 2 }}
                        value={welcomeMessage}
                        onChange={(e) => setWelcomeMessage(e.target.value)}
                        disabled={loading || saving}
                    />
                    <TextField
                        label={t('adminChatbotForm.locale', 'Locale')}
                        select
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        value={normalizedLocale}
                        onChange={(e) => setLocale(e.target.value)}
                        disabled={loading || saving}
                    >
                        <MenuItem value="en">{t('adminChatbotForm.localeEnglish', 'English')}</MenuItem>
                        <MenuItem value="ja">{t('adminChatbotForm.localeJapanese', 'Japanese')}</MenuItem>
                    </TextField>
                    <TextField
                        label={t('adminChatbotForm.minimizedBubbleLine1', 'Minimized Bubble Line 1')}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        value={bubbleTextLine1}
                        onChange={(e) => setBubbleTextLine1(e.target.value)}
                        disabled={loading || saving}
                    />
                    <TextField
                        label={t('adminChatbotForm.minimizedBubbleLine2', 'Minimized Bubble Line 2')}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        value={bubbleTextLine2}
                        onChange={(e) => setBubbleTextLine2(e.target.value)}
                        disabled={loading || saving}
                    />
                    <TextField
                        label={t('adminChatbotForm.placeholder', 'Placeholder')}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        value={placeholder}
                        onChange={(e) => setPlaceholder(e.target.value)}
                        disabled={loading || saving}
                    />

                    <TextField
                        label={t('adminChatbotForm.initialSuggestions', 'Initial Suggestions (one per line)')}
                        multiline
                        fullWidth
                        minRows={6}
                        placeholder={'NetBotとは?\nNetBotの提供サービスは？\nNetBotの独自技術は何ですか？'}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 2, '& textarea::placeholder': { whiteSpace: 'pre-line' } }}
                        value={suggestionsText}
                        onChange={(e) => setSuggestionsText(e.target.value)}
                        disabled={loading || saving}
                    />

                    <TextField
                        label={t('adminChatbotForm.allowedDomains', 'Allowed Domains (one per line)')}
                        multiline
                        fullWidth
                        minRows={4}
                        placeholder={'example.com\napp.example.com\nhttps://localhost:3000'}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 2, '& textarea::placeholder': { whiteSpace: 'pre-line' } }}
                        value={domainsText}
                        onChange={(e) => handleDomainsTextChange(e.target.value)}
                        disabled={loading || saving}
                        helperText={t('adminChatbotForm.allowedDomainsHelper', 'NetBot domains are allowed by the system. Add only customer domains here.')}
                    />

                    <TextField
                        label={t('adminChatbotForm.footerText', 'Footer Text')}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        value={footerText}
                        onChange={(e) => setFooterText(e.target.value)}
                        disabled={loading || saving}
                    />

                    <TextField
                        label={t('adminChatbotForm.footerUrl', 'Footer URL')}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        value={footerUrl}
                        onChange={(e) => setFooterUrl(e.target.value)}
                        disabled={loading || saving}
                    />

                    <FormControlLabel
                        sx={{ mb: 2, display: 'flex' }}
                        label={t('adminChatbotForm.hidePoweredBy', 'Hide Powered By')}
                        control={
                            <Switch
                                size="small"
                                checked={hidePoweredBy}
                                onChange={(e) => setHidePoweredBy(e.target.checked)}
                                disabled={loading || saving}
                            />
                        }
                    />

                    <FormControlLabel
                        sx={{ mb: 2, display: 'flex' }}
                        label={t('adminChatbotForm.enableLiveAgent', 'Enable Live Agent')}
                        control={
                            <Switch
                                size="small"
                                checked={enableLiveAgent}
                                onChange={(e) => setEnableLiveAgent(e.target.checked)}
                                disabled={loading || saving}
                            />
                        }
                    />

                </Paper>

                {isEdit && (
                    <Paper
                        sx={{
                            flex: '0 0 50%',
                            minWidth: 320,
                            p: 2,
                            backgroundColor: 'grey.100',
                        }}
                        elevation={1}
                    >
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                            {t('adminChatbotForm.embedCode', 'Embed Code')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t('adminChatbotForm.embedCodeInstructions', 'Copy and paste this snippet into the page where you want to embed this chatbot.')}
                        </Typography>
                        <Box sx={{ position: 'relative' }}>
                            <TextField
                                value={embedCode}
                                multiline
                                fullWidth
                                minRows={6}
                                maxRows={10}
                                InputProps={{
                                    readOnly: true,
                                    sx: {
                                        fontFamily: 'monospace',
                                        fontSize: '0.8rem',
                                        backgroundColor: '#2b2b2b',
                                        color: 'common.white',
                                        pr: 5,
                                    },
                                }}
                            />
                            <Tooltip title={t('adminChatbotForm.copyEmbedCode', 'Copy embed code')} arrow>
                                <IconButton
                                    size="small"
                                    onClick={handleCopyEmbedCode}
                                    aria-label={t('adminChatbotForm.copyEmbedCode', 'Copy embed code')}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        color: 'common.white',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Paper>
                )}
                
            </Box>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default AdminChatbotForm;
